import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { ClassStore } from '../../stores/class.store';
import { UserStore } from '../../stores/user.store';

// Legacy shipped three documents; this app serves all three from one.
// `base` is the tab title with no section open, and the prefix when one is.
const DOCUMENT_METADATA = {
  instructor: { base: 'iLit Horizon Teacher', favicon: 'favicon_inst.png' },
  student: { base: 'iLit Horizon Student', favicon: 'favicon_stud.png' },
  projection: { base: 'Projector', favicon: 'favicon_projection.png' },
} as const;

/** Legacy's three apps, now three modes of this one. */
export type AppMode = keyof typeof DOCUMENT_METADATA;

/**
 * Owns the document's title and icon link. The entry comes from the route when one
 * declares it, otherwise from the signed-in role, so it stays right when the role
 * resolves after bootstrap without a navigation.
 */
@Injectable({ providedIn: 'root' })
export class DocumentMetadataService {
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly userStore = inject(UserStore);
  private readonly classStore = inject(ClassStore);

  private readonly routeMode = signal<AppMode | null>(null);
  private readonly section = signal<string | null>(null);

  readonly mode = computed<AppMode>(
    () =>
      this.routeMode() ??
      (this.userStore.isStudent() || this.classStore.isStudent() ? 'student' : 'instructor'),
  );

  constructor() {
    effect(() => {
      const { base, favicon } = DOCUMENT_METADATA[this.mode()];
      const section = this.section();
      this.title.setTitle(section ? `${base} - ${section}` : base);
      this.applyFavicon(favicon);
    });
  }

  /** Called by `DocumentTitleStrategy` after every navigation. */
  setRoute(mode: AppMode | null, section: string | null): void {
    this.routeMode.set(mode);
    this.section.set(section);
  }

  // `type` only disambiguates between several icon links; with one, a wrong value
  // risks the browser rejecting it, so we omit it entirely.
  private applyFavicon(href: string): void {
    let icon = this.document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!icon) {
      icon = this.document.createElement('link');
      this.document.head.appendChild(icon);
    }
    icon.setAttribute('rel', 'icon');
    icon.removeAttribute('type');
    if (icon.getAttribute('href') !== href) icon.setAttribute('href', href);
  }
}
